import React, { useEffect, useRef, useState } from "react";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";

const options = [
  { value: "hamburger", label: "Hamburger Icon" },
  { value: "hamburger_with_text", label: "Hamburger + 'Menu'" },
  { value: "menu_text", label: "'Menu' Text Only" },
  { value: "vertical_dots", label: "Three Vertical Dots" },
  { value: "horizontal_dots", label: "Three Horizontal Dots" },
  { value: "plus", label: "Plus" },
  { value: "grid", label: "3x3 Grid" },
];

const MenuIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
  </svg>
);

export default function MobileMenuIconVariantField({ label, help, value, onChange, choices }) {
  const normalizedChoices = choices && choices.length
    ? choices
    : options;

  const containerRef = useRef(null);
  const [numCols, setNumCols] = useState(3);

  useEffect(() => {
    if (!containerRef.current) return;

    const MIN_COLS = 3;
    const MAX_COLS = 7;
    const MIN_TILE_WIDTH_PX = 96; // approximate min width per option incl. padding

    const computeCols = (width) => {
      if (!width || isNaN(width)) return MIN_COLS;
      const cols = Math.floor(width / MIN_TILE_WIDTH_PX);
      return Math.max(MIN_COLS, Math.min(MAX_COLS, cols));
    };

    const update = (width) => setNumCols(computeCols(width));

    // Initial measure
    update(containerRef.current.offsetWidth);

    // Observe size changes of the container (tracks EditPanel width changes)
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        update(cr.width);
      }
    });
    ro.observe(containerRef.current);

    // Fallback: also update on window resize
    const onResize = () => update(containerRef.current?.offsetWidth || 0);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, []);

  const renderPreview = (val) => {
    switch (val) {
      case "hamburger":
        return (
          <div className="flex items-center justify-center h-10">
            <MenuIcon />
          </div>
        );
      case "hamburger_with_text":
        return (
          <div className="flex items-center justify-center h-10 space-x-2">
            <MenuIcon />
            <span className="text-sm">Menu</span>
          </div>
        );
      case "menu_text":
        return (
          <div className="flex items-center justify-center h-10">
            <span className="text-sm">Menu</span>
          </div>
        );
      case "vertical_dots":
        return (
          <div className="flex items-center justify-center h-10">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="5" r="2"/>
              <circle cx="12" cy="12" r="2"/>
              <circle cx="12" cy="19" r="2"/>
            </svg>
          </div>
        );
      case "horizontal_dots":
        return (
          <div className="flex items-center justify-center h-10">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <circle cx="5" cy="12" r="2"/>
              <circle cx="12" cy="12" r="2"/>
              <circle cx="19" cy="12" r="2"/>
            </svg>
          </div>
        );
      case "plus":
        return (
          <div className="flex items-center justify-center h-10">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5v14M5 12h14" strokeLinecap="round"/>
            </svg>
          </div>
        );
      case "grid":
        return (
          <div className="flex items-center justify-center h-10">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="4" height="4" rx="1"/>
              <rect x="10" y="4" width="4" height="4" rx="1"/>
              <rect x="16" y="4" width="4" height="4" rx="1"/>
              <rect x="4" y="10" width="4" height="4" rx="1"/>
              <rect x="10" y="10" width="4" height="4" rx="1"/>
              <rect x="16" y="10" width="4" height="4" rx="1"/>
              <rect x="4" y="16" width="4" height="4" rx="1"/>
              <rect x="10" y="16" width="4" height="4" rx="1"/>
              <rect x="16" y="16" width="4" height="4" rx="1"/>
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center h-10">
            <MenuIcon />
          </div>
        );
    }
  };

  return (
    <div className="field-wrapper">
      {label && <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>{label}</label>}
      <div
        ref={containerRef}
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))` }}
      >
        {normalizedChoices.map((opt) => {
          const isSelected = (value || "").toLowerCase() === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              className={`border rounded-lg p-2 text-center transition-all ${
                isSelected 
                  ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm` 
                  : `${textColors.primary} ${borders.default} ${surfaces.tertiary} ${surfaces.hover}`
              }`}
              onClick={() => onChange(opt.value)}
            >
              {renderPreview(opt.value)}
            </button>
          );
        })}
      </div>
    </div>
  );
}


